import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { UtilsService } from '../../../../../shared/utils/utils.service';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';
import { ApiService } from '../../../../../core/services/api/api.service';
import { cloneDeep } from 'lodash-es';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-modal-affiliate-payment',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatDialogModule,
        MatProgressSpinnerModule
    ],
    templateUrl: './modal-affiliate-payment.component.html',
    styleUrl: './modal-affiliate-payment.component.scss'
})
export class ModalAffiliatePaymentComponent implements OnInit, OnDestroy {
  paymentForm: FormGroup = new FormGroup({});
  showCloseAlert = false;
  isUpdatingPayment = false;

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalAffiliatePaymentComponent>,
    private utils: UtilsService,
    private alert: AlertService,
    private toast: ToastService,
    private api: ApiService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    const { affiliatePaymentId, totalToPay, totalPaid } = receivedData;
    if (!affiliatePaymentId || !this.utils.hasSomething(totalToPay) || !this.utils.hasSomething(totalPaid)) return this.closeModal();

    this.paymentForm = new FormGroup({
      affiliatePaymentId: new FormControl(affiliatePaymentId || null, Validators.required),
      totalPaid: new FormControl(totalPaid || null, [Validators.required, Validators.min(0)])
    });

    this.listenForChanges();
  }

  listenForChanges(): void {
    this.paymentForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.showCloseAlert = true;
    });
  }

  async payToAffiliatePayment(): Promise<void> {
    try {
      this.isUpdatingPayment = true;
      const body = { ...this.paymentForm.value };
      const response = await firstValueFrom(this.api.payToAffiliatePayment(body));
      this.isUpdatingPayment = false;
      this.toast.presentSuccessToast(response.message);
      this.closeModal({ paymentUpdated: response.paymentUpdated });
    } catch (errorResponse: any) {
      this.isUpdatingPayment = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error actualizando pago');
    }
  }
}
